import React from 'react';
import {
  Autocomplete,
  TextField,
  Chip,
  Box,
  Typography,
  CircularProgress,
  ListItem,
  ListItemText,
} from '@mui/material';

import { EntityAssignment, EntityType, EntityTypeOptions } from '../../types/workflow';
import { useEntitySearch } from './hooks/useEntitySearch';
import { EntityOption } from './EntityOptionItem';
import { getEntityChipColor, getEntityChipVariant, getEntityIcon, entityFilterOptions } from './entityUtils';

interface EntityAssignmentSelectorProps {
  value?: EntityAssignment | null;
  onChange: (value: EntityAssignment | null) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  allowedEntityTypes?: EntityType[];
}

/**
 * EntityAssignmentSelector Component
 *
 * A single-select dropdown for selecting a Farmer/Agent/Lab entity.
 * Uses the consolidated /api/workflow/entities/search endpoint via useEntitySearch hook.
 * Supports hybrid filtering: instant client-side + debounced server-side refresh.
 */
export const EntityAssignmentSelector: React.FC<EntityAssignmentSelectorProps> = ({
  value,
  onChange,
  label = 'Select Entity',
  required = false,
  disabled = false,
  error = false,
  helperText,
  allowedEntityTypes,
}) => {
  const {
    open,
    inputValue,
    setInputValue,
    options,
    loading,
    handleOpen,
    handleClose,
    debouncedFetch,
  } = useEntitySearch({ allowedEntityTypes });

  // Handle input change for search
  const handleInputChange = (
    _event: React.SyntheticEvent,
    newInputValue: string
  ) => {
    setInputValue(newInputValue);
    debouncedFetch(newInputValue);
  };

  // Handle selection change
  const handleChange = (
    _event: React.SyntheticEvent,
    newValue: EntityOption | null
  ) => {
    if (newValue) {
      onChange({
        entityType: newValue.entityType,
        entityId: newValue.entityId,
        entityName: newValue.entityName,
        email: newValue.email,
        isSageReady: newValue.isSageReady,
        hasSageSalesRepLink: newValue.hasSageSalesRepLink,
      });
    } else {
      onChange(null);
    }
  };

  // Convert value to option format
  const selectedOption: EntityOption | null = value
    ? {
        entityType: value.entityType as EntityType,
        entityId: value.entityId,
        entityName: value.entityName,
        email: value.email,
        isSageReady: value.isSageReady ?? false,
        hasSageSalesRepLink: value.hasSageSalesRepLink,
      }
    : null;

  // Check if an option is equal to another
  const isOptionEqualToValue = (
    option: EntityOption,
    val: EntityOption
  ): boolean => {
    return option.entityType === val.entityType && option.entityId === val.entityId;
  };

  // Get option label
  const getOptionLabel = (option: EntityOption): string => {
    return option.entityName;
  };

  // Group options by entity type
  const groupBy = (option: EntityOption): string => {
    const typeOption = EntityTypeOptions.find((t) => t.value === option.entityType);
    return typeOption?.label || option.entityType;
  };

  return (
    <Autocomplete
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      value={selectedOption}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      options={options}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      groupBy={groupBy}
      loading={loading}
      disabled={disabled}
      filterOptions={entityFilterOptions}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={error}
          helperText={helperText}
          slotProps={{
            input: {
              ...params.InputProps,
              startAdornment: selectedOption ? (
                <Chip
                  icon={getEntityIcon(selectedOption.entityType) as React.ReactElement}
                  label={selectedOption.entityType}
                  size="small"
                  color={getEntityChipColor(selectedOption.entityType)}
                  variant={getEntityChipVariant(selectedOption.entityType)}
                  sx={{ mr: 0.5 }}
                />
              ) : undefined,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      renderOption={(props, option) => {
        const { key: _key, ...rest } = props;
        return (
          <ListItem key={`${option.entityType}-${option.entityId}`} {...rest} dense>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                gap: 1,
              }}
            >
              <Chip
                icon={getEntityIcon(option.entityType) as React.ReactElement}
                label={option.entityType}
                size="small"
                color={getEntityChipColor(option.entityType)}
                variant={getEntityChipVariant(option.entityType)}
                sx={{ minWidth: 85 }}
              />
              <ListItemText
                primary={option.entityName}
                secondary={option.secondaryText}
                primaryTypographyProps={{ variant: 'body2' }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </Box>
          </ListItem>
        );
      }}
      noOptionsText={
        loading ? (
          'Loading...'
        ) : (
          <Typography variant="body2" color="text.secondary">
            No entities found. Try a different search term.
          </Typography>
        )
      }
      sx={(theme) => ({
        '& .MuiAutocomplete-groupLabel': {
          fontWeight: 600,
          backgroundColor: 'grey.100',
        },
        ...theme.applyStyles('dark', {
          '& .MuiAutocomplete-groupLabel': { backgroundColor: 'grey.800' },
        }),
      })}
    />
  );
};

export default EntityAssignmentSelector;
